import type { Tables } from "~/api/tables";
import { db } from "~/api/db";

import {
  exportDateStamp,
  exportMoney,
  isOutstanding,
  isWithinRange,
  type DateRange,
} from "./report-utils";

type AccountantInvoice = Pick<
  Tables<"invoices">,
  | "client_id"
  | "due_date"
  | "gst_cents"
  | "invoice_number"
  | "issue_date"
  | "notes"
  | "paid_at"
  | "status"
  | "subtotal_cents"
  | "total_cents"
>;

type AccountantExportInput = {
  clientById: ReadonlyMap<string, { name: string }>;
  invoiceRecords: AccountantInvoice[];
  range: DateRange;
  workspace: { id: string; name: string };
};

export async function exportAccountantLedger({
  clientById,
  invoiceRecords,
  range,
  workspace,
}: AccountantExportInput) {
  const [
    { data: expenses, error: expenseError },
    { data: paymentRows, error: paymentError },
    { data: allInvoices, error: allInvoicesError },
    { data: mileage, error: mileageError },
    { data: assets, error: assetsError },
    { data: quotes, error: quotesError },
  ] = await Promise.all([
    db
      .from("expenses")
      .select(
        "expense_date,supplier,description,total_cents,entered_gst_cents,business_use_percentage,claimable_total_cents,claimable_gst_cents,notes,receipt_filename,expense_categories(name)",
      )
      .eq("workspace_id", workspace.id)
      .order("expense_date", { ascending: true }),
    db
      .from("invoice_payments")
      .select("*")
      .eq("workspace_id", workspace.id)
      .order("paid_at", { ascending: true }),
    db.from("invoices").select("*").eq("workspace_id", workspace.id),
    db
      .from("mileage_entries")
      .select("*")
      .eq("workspace_id", workspace.id)
      .order("travelled_on", { ascending: true }),
    db
      .from("fixed_assets")
      .select("*, asset_depreciation_entries(*)")
      .eq("workspace_id", workspace.id)
      .order("purchase_date", { ascending: true }),
    db
      .from("quotes")
      .select("*")
      .eq("workspace_id", workspace.id)
      .order("issue_date", { ascending: true }),
  ]);
  if (expenseError) throw expenseError;
  if (paymentError) throw paymentError;
  if (allInvoicesError) throw allInvoicesError;
  const invoiceById = new Map((allInvoices ?? []).map((row) => [row.id, row]));
  const payments = (paymentRows ?? []).map((payment) => ({
    ...payment,
    invoices: invoiceById.get(payment.invoice_id ?? "") ?? {
      client_id: null,
      invoice_number: "",
    },
  }));
  const expenseRows = (expenses ?? []) as unknown as Array<
    Tables<"expenses"> & { expense_categories: { name: string } | null }
  >;
  const assetRows = (assets ?? []) as unknown as Array<
    Tables<"fixed_assets"> & {
      asset_depreciation_entries: Tables<"asset_depreciation_entries">[];
    }
  >;
  if (mileageError) throw mileageError;
  if (assetsError) throw assetsError;
  if (quotesError) throw quotesError;

  const ledgerInvoices = invoiceRecords.filter((invoice) =>
    isWithinRange(invoice.issue_date, range),
  );
  const ledgerExpenses = expenseRows.filter((expense) =>
    isWithinRange(expense.expense_date, range),
  );
  const ledgerPayments = payments.filter((payment) =>
    isWithinRange(payment.paid_at, range),
  );
  const ledgerMileage = (mileage ?? []).filter((entry) =>
    isWithinRange(entry.travelled_on, range),
  );
  const ledgerQuotes = (quotes ?? []).filter((quote) =>
    isWithinRange(quote.issue_date, range),
  );
  const invoiceGross = ledgerInvoices.reduce(
    (sum, invoice) => sum + invoice.total_cents,
    0,
  );
  const invoiceGst = ledgerInvoices.reduce(
    (sum, invoice) => sum + invoice.gst_cents,
    0,
  );
  const expenseGross = ledgerExpenses.reduce(
    (sum, expense) => sum + (expense.claimable_total_cents ?? 0),
    0,
  );
  const expenseGst = ledgerExpenses.reduce(
    (sum, expense) => sum + (expense.claimable_gst_cents ?? 0),
    0,
  );
  const paymentsReceived = ledgerPayments.reduce(
    (sum, payment) => sum + payment.amount_cents,
    0,
  );
  const mileageClaim = ledgerMileage.reduce(
    (sum, entry) => sum + (entry.claim_cents ?? 0),
    0,
  );
  const depreciationClaim = assetRows.reduce(
    (total, asset) =>
      total +
      asset.asset_depreciation_entries
        .filter((entry) => isWithinRange(`${entry.income_year}-03-31`, range))
        .reduce(
          (sum, entry) =>
            sum +
            entry.investment_boost_claim_cents +
            entry.depreciation_claim_cents,
          0,
        ),
    0,
  );
  const outstanding = invoiceRecords
    .filter(isOutstanding)
    .reduce((sum, invoice) => sum + invoice.total_cents, 0);

  const { saveCsvExport } = await import("~/utils/export-file");
  await saveCsvExport(`mintstint-accountant-ledger-${exportDateStamp()}.csv`, [
    ["MintStint accountant export", workspace.name],
    ["Period start", range.start?.toISOString().slice(0, 10) ?? "All records"],
    ["Period end", range.end?.toISOString().slice(0, 10) ?? "All records"],
    ["Basis", "Invoice/accrual records plus a separate cash-receipts trail"],
    [
      "Important",
      "Record-keeping export only; classification and tax treatment require accountant review",
    ],
    [],
    ["Summary", "Net NZD", "GST NZD", "Gross NZD"],
    [
      "Invoices issued",
      exportMoney(invoiceGross - invoiceGst),
      exportMoney(invoiceGst),
      exportMoney(invoiceGross),
    ],
    [
      "Claimable business expenses",
      exportMoney(expenseGross - expenseGst),
      exportMoney(expenseGst),
      exportMoney(expenseGross),
    ],
    ["Payments received", "", "", exportMoney(paymentsReceived)],
    [
      "Mileage claims recorded",
      exportMoney(mileageClaim),
      "",
      exportMoney(mileageClaim),
    ],
    [
      "Investment Boost and depreciation estimates",
      exportMoney(depreciationClaim),
      "",
      exportMoney(depreciationClaim),
    ],
    ["Accounts receivable at export date", "", "", exportMoney(outstanding)],
    [],
    [
      "Record type",
      "Date",
      "Reference",
      "Party",
      "Category/status",
      "Description",
      "Net NZD",
      "GST NZD",
      "Gross NZD",
      "Business use %",
      "Receipt",
      "Notes",
    ],
    ...ledgerInvoices.map((invoice) => [
      "Invoice",
      invoice.issue_date,
      invoice.invoice_number,
      invoice.client_id
        ? (clientById.get(invoice.client_id)?.name ?? "Unknown client")
        : "",
      invoice.status,
      "Services invoiced",
      exportMoney(invoice.subtotal_cents),
      exportMoney(invoice.gst_cents),
      exportMoney(invoice.total_cents),
      "100",
      "",
      invoice.notes ?? "",
    ]),
    ...ledgerPayments.map((payment) => {
      const invoice = payment.invoices as {
        client_id: string | null;
        invoice_number: string;
      };
      return [
        "Payment received",
        payment.paid_at,
        payment.reference ?? invoice.invoice_number,
        invoice.client_id
          ? (clientById.get(invoice.client_id)?.name ?? "Unknown client")
          : "",
        "Cash receipt",
        `Payment for ${invoice.invoice_number}`,
        "",
        "",
        exportMoney(payment.amount_cents),
        "",
        "",
        payment.note ?? "",
      ];
    }),
    ...ledgerExpenses.map((expense) => [
      "Expense",
      expense.expense_date,
      "",
      expense.supplier,
      (expense.expense_categories as { name: string } | null)?.name ??
        "Uncategorised",
      expense.description,
      exportMoney(
        (expense.claimable_total_cents ?? 0) -
          (expense.claimable_gst_cents ?? 0),
      ),
      exportMoney(expense.claimable_gst_cents ?? 0),
      exportMoney(expense.claimable_total_cents ?? 0),
      String(expense.business_use_percentage),
      expense.receipt_filename ?? "",
      expense.notes ?? "",
    ]),
    ...ledgerMileage.map((entry) => [
      "Mileage",
      entry.travelled_on,
      entry.registration ?? "",
      entry.vehicle_name,
      `Income year ${entry.income_year}; tier ${entry.rate_tier}`,
      `${entry.purpose}; ${Number(entry.business_km).toFixed(1)} km @ ${Number(entry.rate_cents_per_km).toFixed(2)} cents/km${entry.origin || entry.destination ? `; ${entry.origin ?? ""} to ${entry.destination ?? ""}` : ""}`,
      exportMoney(entry.claim_cents ?? 0),
      "",
      exportMoney(entry.claim_cents ?? 0),
      "100",
      "",
      entry.notes ?? "",
    ]),
    ...assetRows.flatMap((asset) => [
      [
        "Fixed asset",
        asset.purchase_date,
        asset.asset_number ?? "",
        "",
        asset.category,
        `${asset.name}; available ${asset.in_service_date}; ${asset.depreciation_method} ${asset.depreciation_rate}%`,
        exportMoney(asset.depreciable_cost_cents),
        exportMoney(asset.gst_cents),
        exportMoney(asset.original_cost_cents),
        String(asset.business_use_percent),
        "",
        asset.notes ?? "",
      ],
      ...asset.asset_depreciation_entries
        .filter((entry) => isWithinRange(`${entry.income_year}-03-31`, range))
        .map((entry) => [
          "Asset depreciation",
          `${entry.income_year}-03-31`,
          asset.asset_number ?? asset.id,
          "",
          entry.is_estimate ? "Estimate — accountant review" : "Confirmed",
          `${asset.name}; opening ATV ${exportMoney(entry.opening_adjusted_value_cents)}; Investment Boost claim ${exportMoney(entry.investment_boost_claim_cents)}; depreciation claim ${exportMoney(entry.depreciation_claim_cents)}; closing ATV ${exportMoney(entry.closing_adjusted_value_cents)}`,
          exportMoney(
            entry.investment_boost_claim_cents + entry.depreciation_claim_cents,
          ),
          "",
          exportMoney(
            entry.investment_boost_claim_cents + entry.depreciation_claim_cents,
          ),
          String(asset.business_use_percent),
          "",
          entry.notes ?? "",
        ]),
    ]),
    ...ledgerQuotes.map((quote) => [
      "Quote (non-ledger)",
      quote.issue_date,
      quote.quote_number,
      quote.client_id
        ? (clientById.get(quote.client_id)?.name ?? "Unknown client")
        : "",
      quote.status,
      quote.title,
      exportMoney(quote.subtotal_cents),
      exportMoney(quote.gst_cents),
      exportMoney(quote.total_cents),
      "",
      "",
      "Not recognised as income until invoiced/earned under the applicable basis",
    ]),
  ]);
}
